/**
 * Loose JSON shape the Android stethoscope publisher embeds in each Janus
 * Streaming mountpoint's `metadata` field. Every property is optional so the
 * web client renders whatever the publisher sent.
 *
 * @typedef {Object} StethoscopeMetadata
 * @property {number} [schema]               Schema version (2+ includes hospital, 3+ includes assistance)
 * @property {number} [sessionStartedAt]
 * @property {{ id?: number, name?: string }} [operator]
 * @property {{ id?: number, name?: string }} [patient]
 * @property {{ id?: number, name?: string }} [hospital]   Selected facility
 * @property {{ mac?: string, firmware?: string }} [device]
 * @property {{ id: string, label: string, side: string }} [placement]
 * @property {string} [mode]
 * @property {boolean} [noiseReduction]
 * @property {string} [codec]
 * @property {number} [heartRateBpm]
 * @property {number} [heartRateAt]
 * @property {{ versionName?: string, versionCode?: number }} [app]
 * @property {{ roomId: number, plugin?: string }} [assistance]
 */

/**
 * @param {unknown} raw
 * @returns {StethoscopeMetadata | null}
 */
export function parseMetadata (raw) {
	if (typeof raw !== 'string' || raw.length === 0) return null;
	try {
		const obj = JSON.parse(raw);
		if (obj && typeof obj === 'object') return obj;
		return null;
	} catch {
		return null;
	}
}

/**
 * Returns "12s", "2m 05s" or "1h 02m" depending on age — null if missing.
 * @param {number | undefined} startedAt
 */
export function formatSessionAge (startedAt) {
	if (!startedAt || startedAt <= 0) return null;
	const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
	if (seconds < 60) return `${seconds}s`;
	const mins = Math.floor(seconds / 60);
	const rem = seconds % 60;
	if (mins < 60) return `${mins}m ${String(rem).padStart(2, '0')}s`;
	const hrs = Math.floor(mins / 60);
	return `${hrs}h ${String(mins % 60).padStart(2, '0')}m`;
}

/**
 * TextRoom id published by Android in `metadata.assistance.roomId` (schema v3+).
 * Coerces string/number and accepts legacy `room_id` spelling.
 *
 * @param {StethoscopeMetadata | null | undefined} metadata
 * @returns {number | null}
 */
export function getAssistanceRoomId (metadata) {
	if (!metadata || typeof metadata !== 'object') return null;
	const block = metadata.assistance;
	if (!block || typeof block !== 'object') return null;
	const raw = block.roomId ?? block.room_id;
	const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : Number(raw);
	return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Human-readable age of a heart-rate reading, e.g. "12s ago" or "2m ago".
 * Returns null if `at` is missing/invalid.
 * @param {number | undefined} at
 */
export function formatHeartRateAge (at) {
	if (!at || at <= 0) return null;
	const seconds = Math.max(0, Math.floor((Date.now() - at) / 1000));
	if (seconds < 1) return 'just now';
	if (seconds < 60) return `${seconds}s ago`;
	const mins = Math.floor(seconds / 60);
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	return `${hrs}h ago`;
}
